import {
  FUNDRAISER_DONATION_PURCHASE_MESSAGES,
  STORE_DONATION_MARKUP_AMOUNT_LIMITS,
} from '../constants/storeCheckout.constants';

export type StoreReviewStoreType =
  | 'group-order'
  | 'brand-drop'
  | 'fundraiser'
  | 'permanent-store'
  | undefined;

export const getInitialStoreReviewDonationAmounts = (
  controlledDonationAmounts: Record<string, string> | undefined
): Record<string, string> => {
  if (controlledDonationAmounts !== undefined) {
    return {};
  }

  return {};
};

/**
 * Build donation/markup input values keyed like StoreReviewOrderStep
 * (`donation-${groupIndex}-${itemIndex}`) from hydrated cart items.
 */
export const buildStoreReviewDonationAmountsFromCartItems = (
  items: Array<{
    designGroupItemId?: number | null;
    proofItemId?: number;
    proofId?: number;
    donationAmount?: string;
  }>
): Record<string, string> => {
  if (!items.length) {
    return {};
  }

  const groupedItems = items.reduce<Record<string | number, Array<{ donationAmount?: string }>>>(
    (acc, item) => {
      const groupKey = item.designGroupItemId ?? item.proofItemId ?? item.proofId ?? 0;

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }

      acc[groupKey].push(item);

      return acc;
    },
    {}
  );

  return Object.values(groupedItems).reduce<Record<string, string>>(
    (amounts, groupItems, groupIndex) => {
      groupItems.forEach((item, itemIndex) => {
        const amount = item.donationAmount?.trim() ?? '';

        if (!amount) {
          return;
        }

        amounts[`donation-${groupIndex}-${itemIndex}`] = amount;
      });

      return amounts;
    },
    {}
  );
};

export function hasPositiveDonationAmount(amount: string | undefined): boolean {
  const trimmed = amount?.trim() ?? '';
  if (!trimmed) {
    return false;
  }

  const parsed = parseFloat(trimmed);
  return (
    !Number.isNaN(parsed) &&
    parsed >= STORE_DONATION_MARKUP_AMOUNT_LIMITS.MIN &&
    parsed <= STORE_DONATION_MARKUP_AMOUNT_LIMITS.MAX
  );
}

export function hasEnteredFundraiserGoalAmount(raiseAmount?: string): boolean {
  return hasPositiveDonationAmount(raiseAmount);
}

export function shouldShowItemFinalPrice(donationAmount: string): boolean {
  return hasPositiveDonationAmount(donationAmount);
}

export function getItemFinalPriceWithDonation(
  donationAmount: string,
  itemPrice: number | undefined | null
): number {
  const pricePerItem = itemPrice ?? 0;
  const parsedDonation = parseFloat(donationAmount.trim());
  return pricePerItem + (Number.isNaN(parsedDonation) ? 0 : parsedDonation);
}

export function getStoreReviewBreakpointDisplayPrice(
  baseBreakpointPrice: number,
  donationAmount: string | undefined,
  includeDonation: boolean
): number {
  if (!includeDonation) {
    return baseBreakpointPrice;
  }

  return getItemFinalPriceWithDonation(donationAmount ?? '', baseBreakpointPrice);
}

export function getFundraiserDonationPurchaseMessage(
  formattedDonationAmount: string,
  charityName?: string
): string {
  const trimmedCharity = charityName?.trim() ?? '';

  if (trimmedCharity) {
    return FUNDRAISER_DONATION_PURCHASE_MESSAGES.WITH_CHARITY.replace(
      '{amount}',
      formattedDonationAmount
    ).replace('{charityName}', trimmedCharity);
  }

  return FUNDRAISER_DONATION_PURCHASE_MESSAGES.WITHOUT_CHARITY.replace(
    '{amount}',
    formattedDonationAmount
  );
}
